import { useState } from 'react';

import { faChevronLeft } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLingui } from '@lingui/react/macro';
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  ToggleGroup,
  ToggleGroupItem,
} from '@repo/videa-components/src';
import { Button } from '@repo/videa-components/src/ui/button';
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/videa-components/src/ui/dialog';

import {
  ALIGNER_SEVERITY_OPTIONS,
  SRP_STAGE_OPTIONS,
  type TppProviderSettings,
  useAlignerSeverityLabels,
} from '../../../../react-query/useCoreFourTPP';

import { ProviderPill } from './ProviderPill';

const CROWNS_DMF_MIN = 25;
const CROWNS_DMF_MAX = 70;

const bodyTextClass = 'vai-text-base vai-leading-6 vai-text-zinc-900';

const editCardClass =
  'vai-flex vai-flex-col vai-gap-0.25 vai-rounded-md vai-border vai-border-zinc-200 vai-bg-white vai-p-4';
const editSectionClass =
  'vai-flex vai-flex-col vai-gap-2 vai-rounded-md vai-bg-zinc-100 vai-border vai-border-zinc-200 vai-p-4';

const toggleGroupClass =
  'vai-inline-flex vai-h-10 vai-rounded-md vai-border vai-border-zinc-200 vai-bg-white vai-overflow-hidden vai-p-0 vai-gap-0';
const toggleItemClass =
  'vai-h-10 vai-min-w-0 vai-rounded-none vai-px-4 vai-border-0 vai-border-r vai-border-zinc-200 last:vai-border-r-0 vai-bg-white vai-text-sm vai-font-medium vai-text-zinc-900 vai-transition-colors focus-visible:vai-outline-none focus-visible:vai-ring-2 focus-visible:vai-ring-ring focus-visible:vai-ring-offset-2 disabled:vai-pointer-events-none disabled:vai-opacity-50 data-[state=on]:!vai-bg-blue-50 data-[state=on]:!vai-text-blue-600 hover:vai-bg-zinc-50 data-[state=on]:hover:!vai-bg-blue-50';

const backButtonClass =
  'vai-flex vai-cursor-pointer vai-items-center vai-gap-1.5 vai-shrink-0 vai-rounded vai-text-sm vai-text-blue-600 hover:vai-underline focus:vai-outline-none focus:vai-ring-2 focus:vai-ring-ring focus:vai-ring-offset-2';

const labelTextClass =
  'vai-text-[12px] vai-leading-4 vai-text-muted-foreground vai-font-normal';

export interface EditCoreFourCriteriaViewProps {
  initialSettings: TppProviderSettings;
  providerName?: string;
  onSave: (settings: TppProviderSettings) => void;
  onCancel: () => void;
  isSaving: boolean;
  lastUpdated: { date?: string; by: string } | null;
  lastUpdatedDateStr: string;
  lastUpdatedBy: string;
}

export const EditCoreFourCriteriaView = ({
  initialSettings,
  providerName,
  onSave,
  onCancel,
  isSaving,
  lastUpdated,
  lastUpdatedDateStr,
  lastUpdatedBy,
}: EditCoreFourCriteriaViewProps) => {
  const { t } = useLingui();
  const alignerSeverityLabels = useAlignerSeverityLabels();
  const [editDraft, setEditDraft] =
    useState<TppProviderSettings>(initialSettings);

  const hasCrownsDmfError =
    editDraft.crownDmfThreshold < CROWNS_DMF_MIN ||
    editDraft.crownDmfThreshold > CROWNS_DMF_MAX;

  const handleSave = () => {
    onSave(editDraft);
  };

  return (
    <>
      <button
        type="button"
        onClick={onCancel}
        className={backButtonClass}
        data-testid="edit-criteria-back"
      >
        <FontAwesomeIcon
          icon={faChevronLeft}
          className="vai-h-4 vai-w-4 vai-text-blue-600"
        />
        <span>{t`Back`}</span>
      </button>
      <DialogHeader className="vai-shrink-0 vai-gap-1.5 vai-text-left">
        <DialogTitle className="vai-text-2xl vai-font-semibold vai-leading-8 vai-text-slate-900">
          {t`Treatment Criteria`}
        </DialogTitle>
        <DialogDescription className="vai-text-sm vai-leading-5 vai-text-muted-foreground">
          {t`Edit Core-Four TPP treatment criteria`}
        </DialogDescription>
      </DialogHeader>
      {providerName && <ProviderPill name={providerName} className="vai-w-fit" />}
      <div className={editSectionClass}>
        <div className={editCardClass}>
          <h4 className="vai-text-base vai-font-medium vai-leading-6 vai-text-slate-900">
            {t`Crowns`}
          </h4>
          <div className="vai-flex vai-flex-col vai-gap-1">
            <div className="vai-flex vai-flex-wrap vai-items-center vai-gap-2">
              <span className={bodyTextClass}>
                {t`Treatment plan a crown when DMF is greater than`}
              </span>
              <div className="vai-flex vai-items-center vai-gap-1">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={editDraft.crownDmfThreshold}
                  onChange={(e) => {
                    const v = e.target.valueAsNumber;
                    if (!Number.isNaN(v)) {
                      const clamped = Math.min(100, Math.max(0, v));
                      setEditDraft((prev) => ({
                        ...prev,
                        crownDmfThreshold: clamped,
                      }));
                    }
                  }}
                  className={`vai-h-10 vai-w-16 vai-text-center ${hasCrownsDmfError ? 'vai-border-red-500 focus-visible:vai-ring-red-500' : 'vai-border-blue-600'}`}
                />
                <span className="vai-text-sm vai-text-muted-foreground">
                  %
                </span>
              </div>
            </div>
            {hasCrownsDmfError && (
              <p className="vai-text-sm vai-text-red-600" role="alert">
                {t`DMF must be between ${CROWNS_DMF_MIN} and ${CROWNS_DMF_MAX}`}
              </p>
            )}
          </div>
        </div>
        <div className={editCardClass}>
          <h4 className="vai-text-base vai-font-medium vai-leading-6 vai-text-slate-900">
            {t`SRP`}
          </h4>
          <div className="vai-flex vai-flex-wrap vai-items-center vai-gap-2">
            <span className={bodyTextClass}>
              {t`Treatment plan SRP when patient has`}
            </span>
            <Select
              value={String(editDraft.srpPerioStageThreshold)}
              onValueChange={(value) =>
                setEditDraft((prev) => ({
                  ...prev,
                  srpPerioStageThreshold: Number(value) as TppProviderSettings['srpPerioStageThreshold'],
                }))
              }
            >
              <SelectTrigger className="vai-w-auto vai-min-w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SRP_STAGE_OPTIONS.map((opt) => (
                  <SelectItem key={opt} value={String(opt)}>
                    {t`Stage ${opt}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className={bodyTextClass}>
              {t`or higher periodontal disease`}
            </span>
          </div>
        </div>
        <div className={editCardClass}>
          <h4 className="vai-text-base vai-font-medium vai-leading-6 vai-text-slate-900">
            {t`Curodont`}
          </h4>
          <div className="vai-flex vai-flex-wrap vai-items-center vai-gap-2">
            <span className={bodyTextClass}>
              {t`Do you treatment plan Curodont for enamel-only lesions?`}
            </span>
            <ToggleGroup
              type="single"
              value={editDraft.incipientEnabled ? 'yes' : 'no'}
              onValueChange={(v) => {
                if (v)
                  setEditDraft((prev) => ({
                    ...prev,
                    incipientEnabled: v === 'yes',
                  }));
              }}
              className={toggleGroupClass}
            >
              <ToggleGroupItem value="yes" className={toggleItemClass}>
                {t`Yes`}
              </ToggleGroupItem>
              <ToggleGroupItem value="no" className={toggleItemClass}>
                {t`No`}
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>
        <div className={editCardClass}>
          <h4 className="vai-text-base vai-font-medium vai-leading-6 vai-text-slate-900">
            {t`Invisalign`}
          </h4>
          <div className="vai-flex vai-flex-col vai-gap-2">
            <div className="vai-flex vai-flex-wrap vai-items-center vai-gap-2">
              <span className={bodyTextClass}>
                {t`Do you treatment plan (or refer) for Invisalign for malocclusion?`}
              </span>
              <ToggleGroup
                type="single"
                value={editDraft.alignersEnabled ? 'yes' : 'no'}
                onValueChange={(v) => {
                  if (v)
                    setEditDraft((prev) => ({
                      ...prev,
                      alignersEnabled: v === 'yes',
                      alignersSeverityThreshold:
                        v === 'yes'
                          ? (prev.alignersSeverityThreshold ?? 'MODERATE')
                          : prev.alignersSeverityThreshold,
                    }));
                }}
                className={toggleGroupClass}
              >
                <ToggleGroupItem value="yes" className={toggleItemClass}>
                  {t`Yes`}
                </ToggleGroupItem>
                <ToggleGroupItem value="no" className={toggleItemClass}>
                  {t`No`}
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
            {editDraft.alignersEnabled && (
              <div className="vai-flex vai-flex-wrap vai-items-center vai-gap-2">
                <span className={bodyTextClass}>
                  {t`Treatment plan Invisalign for only`}
                </span>
                <Select
                  value={editDraft.alignersSeverityThreshold ?? 'MODERATE'}
                  onValueChange={(value) =>
                    setEditDraft((prev) => ({
                      ...prev,
                      alignersSeverityThreshold:
                        value as TppProviderSettings['alignersSeverityThreshold'],
                    }))
                  }
                >
                  <SelectTrigger className="vai-w-auto vai-min-w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ALIGNER_SEVERITY_OPTIONS.map((opt) => (
                      <SelectItem key={opt} value={opt}>
                        {alignerSeverityLabels[opt]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className={bodyTextClass}>
                  {t`cases of malocclusion`}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
      <DialogFooter className="vai-shrink-0 vai-flex vai-items-center vai-justify-between vai-gap-4 vai-pt-2 -vai-mt-2">
        <div className="vai-flex vai-min-w-0 vai-flex-1 vai-items-center vai-justify-start vai-text-left">
          <p className={labelTextClass}>
            {lastUpdated
              ? t`Last Updated: ${lastUpdatedDateStr} by ${lastUpdatedBy}`
              : t`Last Updated: — by —`}
          </p>
        </div>
        <div className="vai-flex vai-items-center vai-gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="vai-shrink-0 vai-border-zinc-200 vai-bg-white hover:vai-bg-zinc-50"
          >
            {t`Cancel`}
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={hasCrownsDmfError || isSaving}
            className="vai-shrink-0 vai-bg-blue-600 hover:vai-bg-blue-700 vai-text-white"
            data-testid="edit-criteria-save"
          >
            {isSaving ? t`Saving…` : t`Save`}
          </Button>
        </div>
      </DialogFooter>
    </>
  );
};
